import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TablePagination,
  IconButton,
  Tooltip
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/styles';
import Page from '../components/Page';
import { useSnackbar } from 'notistack5';
import diceGameApi from 'utils/api/diceGame';
import user_api from 'utils/api/users';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment-timezone';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: '24px',
    paddingBottom: '24px',
  },
  statsCard: {
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  statsValue: {
    fontSize: '36px',
    fontWeight: 'bold',
    marginTop: '8px',
  },
  filterSection: {
    marginBottom: '24px',
    padding: '20px',
    background: '#f5f5f5',
    borderRadius: '8px',
  },
  tableContainer: {
    marginTop: '24px',
  },
  winnerChip: {
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: 'white',
    fontWeight: 'bold',
  },
  loserChip: {
    background: '#e0e0e0',
    color: '#666',
  },
  claimedBadge: {
    background: '#4caf50',
    color: 'white',
  },
  unclaimedBadge: {
    background: '#ff9800',
    color: 'white',
  },
}));

const AdminDiceGame = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('day').format('YYYY-MM-DD'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [configDialog, setConfigDialog] = useState(false);
  const [configCompany, setConfigCompany] = useState('');
  const [maxWinners, setMaxWinners] = useState(10);
  const [savingConfig, setSavingConfig] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [companyStatsPage, setCompanyStatsPage] = useState(0);
  const [companyStatsRowsPerPage, setCompanyStatsRowsPerPage] = useState(6);
  const [storeGroups, setStoreGroups] = useState([]);

  useEffect(() => {
    loadCompanies();
    loadStoreGroups();
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedCompany, startDate, endDate, page, rowsPerPage, statusFilter, groupFilter]);

  const loadCompanies = async () => {
    try {
      const response = await user_api.get_user_company();
      if (response.data) {
        const eligibleCompanies = ['Syzygy', 'Jasem', '7-Star'];
        const filtered = response.data.filter(company => 
          eligibleCompanies.some(eligible => 
            company.toLowerCase().includes(eligible.toLowerCase())
          )
        );
        setCompanies(filtered);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadStoreGroups = async () => {
    try {
      const response = await fetch('/api/groups', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const data = await response.json();
      if (data.success && data.groups) {
        setStoreGroups(data.groups.filter(group => group.companyName));
      }
    } catch (error) {
      console.error('Error loading store groups:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [gamesResponse, statsResponse] = await Promise.all([
        diceGameApi.get_all_dice_games({
          company: selectedCompany,
          startDate,
          endDate,
          page: page + 1,
          limit: rowsPerPage,
          status: statusFilter,
          group: groupFilter
        }),
        diceGameApi.get_dice_game_stats(selectedCompany)
      ]);
      const result = await diceGameApi.get_dice_game_stats(selectedCompany)
      if (gamesResponse.data.success) {
        setGames(gamesResponse.data.games);
        setTotalRecords(gamesResponse.data.pagination.total);
      }

      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      enqueueSnackbar('Error loading data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfigDialog = (company) => {
    setConfigCompany(company);
    setConfigDialog(true);
    loadConfig(company);
  };

  const loadConfig = async (company) => {
    try {
      const response = await diceGameApi.get_dice_game_config(company);
      if (response.data.success) {
        setMaxWinners(response.data.config.maxWinners || 10);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const response = await diceGameApi.update_dice_game_config({
        company: configCompany,
        maxWinners: parseInt(maxWinners),
        isActive: true
      });

      if (response.data.success) {
        enqueueSnackbar('Configuration updated successfully', { variant: 'success' });
        setConfigDialog(false);
        loadData();
      }
    } catch (error) {
      console.error('Error saving config:', error);
      enqueueSnackbar('Error saving configuration', { variant: 'error' });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleMarkClaimed = async (gameId) => {
    try {
      const response = await diceGameApi.mark_prize_claimed(gameId);
      if (response.data.success) {
        enqueueSnackbar('Prize marked as claimed', { variant: 'success' });
        loadData();
      }
    } catch (error) {
      console.error('Error marking prize:', error);
      enqueueSnackbar('Error updating prize status', { variant: 'error' });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCompanyStatsChangePage = (event, newPage) => {
    setCompanyStatsPage(newPage);
  };

  const handleCompanyStatsChangeRowsPerPage = (event) => {
    setCompanyStatsRowsPerPage(parseInt(event.target.value, 10));
    setCompanyStatsPage(0);
  };

  return (
    <Page title="Dice Game Management">
      <Container maxWidth="xl" className={classes.root}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            🎲 Dice Game Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Mid-Autumn Festival Dice Game Results and Configuration
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6">Total Games</Typography>
                <Typography className={classes.statsValue}>
                  {stats.totalGames || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6">Total Winners</Typography>
                <Typography className={classes.statsValue}>
                  {stats.totalWinners || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6">Win Rate</Typography>
                <Typography className={classes.statsValue}>
                  {stats.totalGames > 0 
                    ? ((stats.totalWinners / stats.totalGames) * 100).toFixed(1) 
                    : 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6">Companies</Typography>
                <Typography className={classes.statsValue}>
                  {companies.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Company Statistics */}
        {stats.companyStats && stats.companyStats.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Company Statistics
              </Typography>
              <Grid container spacing={2}>
                {stats.companyStats
                  .slice(companyStatsPage * companyStatsRowsPerPage, (companyStatsPage + 1) * companyStatsRowsPerPage)
                  .map((companyStat, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {companyStat._id}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Games: {companyStat.totalGames} | Winners: {companyStat.totalWinners}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Avg Score: {companyStat.averageScore?.toFixed(1)} | Max: {companyStat.highestScore}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => handleOpenConfigDialog(companyStat._id)} size="small">
                          <SettingsIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              {/* Company Statistics Pagination */}
              <TablePagination
                component="div"
                count={stats.companyStats.length}
                page={companyStatsPage}
                onPageChange={handleCompanyStatsChangePage}
                rowsPerPage={companyStatsRowsPerPage}
                onRowsPerPageChange={handleCompanyStatsChangeRowsPerPage}
                rowsPerPageOptions={[3, 6, 9, 12]}
                labelRowsPerPage="Companies per page:"
              />
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className={classes.filterSection}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                value={selectedCompany}
                onChange={(event, newValue) => setSelectedCompany(newValue || 'all')}
                options={['all', ...companies]}
                getOptionLabel={(option) => option === 'all' ? 'All Companies' : option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="winner">Winners Only</MenuItem>
                  <MenuItem value="loser">Non-Winners Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Store Group</InputLabel>
                <Select
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  label="Store Group"
                >
                  <MenuItem value="all">All Store Groups</MenuItem>
                  {storeGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.companyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={loadData}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Games Table */}
        <Card className={classes.tableContainer}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Game Results
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Player</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Rolls</TableCell>
                        <TableCell align="center">Highest Score</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Prize Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {games.map((game) => (
                        <TableRow key={game._id}>
                          <TableCell>
                            {moment(game.playedDate).format('MMM DD, YYYY HH:mm')}
                          </TableCell>
                          <TableCell>{game.userName}</TableCell>
                          <TableCell>{game.company}</TableCell>
                          <TableCell>{game.userEmail}</TableCell>
                          <TableCell align="center">
                            {game.rolls.map((roll, idx) => (
                              <Chip
                                key={idx}
                                label={`${roll.total}`}
                                size="small"
                                sx={{ m: 0.5 }}
                              />
                            ))}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              {game.highestScore}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {game.isWinner ? (
                              <Chip
                                label="Winner 🎉"
                                className={classes.winnerChip}
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Try Again"
                                className={classes.loserChip}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {game.isWinner && (
                              <Chip
                                label={game.prizeClaimed ? 'Claimed' : 'Unclaimed'}
                                className={game.prizeClaimed ? classes.claimedBadge : classes.unclaimedBadge}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {game.isWinner && !game.prizeClaimed && (
                              <Tooltip title="Mark as Claimed">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleMarkClaimed(game._id)}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  component="div"
                  count={totalRecords}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Configuration Dialog */}
        <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Configure Max Winners - {configCompany}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Winners Per Day"
                value={maxWinners}
                onChange={(e) => setMaxWinners(e.target.value)}
                inputProps={{ min: 1, max: 1000 }}
                helperText="Set the maximum number of winners allowed per day for this company"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfigDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveConfig}
              variant="contained"
              color="primary"
              disabled={savingConfig}
            >
              {savingConfig ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default AdminDiceGame;

